// AllOrders.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import OrderCard from '../../../component/order/orderCard';
import OrderModal from '../../../component/order/OrderModal';
import orderService from '../../../services/orderService';

const AllOrders = () => {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const location = useLocation();

  /* ── Fetch orders (reusable) ── */
  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const res = await orderService.getMyOrders({ sort: 'newest' });
      const next = Array.isArray(res?.data) ? res.data : [];
      setOrders(next);
      return next;
    } catch (err) {
      console.error('Failed to load orders:', err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Could not load your orders. Please try again.'
      );
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /* ── Fetch on mount + whenever the route changes ── */
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders, location.key]);

  /* ── Open the drawer for a specific order ── */
  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  /* ── Refresh after content is saved ──
     Refetches, then re-syncs the currently-open order so its
     hasContent flag is up to date. */
  const handleRefresh = async () => {
    const freshOrders = await fetchOrders();

    /* Re-sync selectedOrder from the fresh list so if the drawer
       stays open it shows the updated hasContent state. */
    if (selectedOrder) {
      const updated = freshOrders.find(
        (o) =>
          String(o.id || o._id) === String(selectedOrder.id || selectedOrder._id)
      );
      if (updated) setSelectedOrder(updated);
    }
  };

  return (
    <div className="px-2 sm:px-0">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
        All Orders
      </h1>
      <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">
        View all your past and current orders
      </p>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#D2A146]" />
        </div>
      )}

      {/* Error */}
      {!isLoading && error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded px-4 py-3">
          {error}
        </div>
      )}

      {/* Empty */}
      {!isLoading && !error && orders.length === 0 && (
        <div className="text-center py-16">
          <p className="text-gray-600 mb-1">
            You haven't placed any orders yet.
          </p>
          <p className="text-sm text-gray-400">
            When you do, they'll appear here.
          </p>
        </div>
      )}

      {/* List */}
      {!isLoading && !error && orders.length > 0 && (
        <div className="space-y-3 sm:space-y-4">
          {orders.map((order) => (
            <div
              key={order.id || order.orderNumber}
              onClick={() => handleViewOrder(order)}
              className="cursor-pointer"
            >
              <OrderCard
                order={order}
                status={order.orderStatus || order.status}
              />
            </div>
          ))}
        </div>
      )}

      <OrderModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        order={selectedOrder}
        onRefresh={handleRefresh}
      />
    </div>
  );
};

export default AllOrders;